// @ts-ignore Canonical shared helper is plain JS for tests and both apps.
import { canvasSize } from "../../../shared/photo-prepare.mjs";

/** Downscale a thermometer photo before AI read and upload so large iPhone captures do not freeze the WebView. */
export async function compressPhoto(file: File, maxWidth = 1200, quality = 0.85): Promise<File> {
  const blob = await renderJpeg(file, maxWidth, quality);
  return new File([blob], "photo.jpg", { type: "image/jpeg" });
}

async function renderJpeg(file: File, maxWidth: number, quality: number): Promise<Blob> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { resizeWidth: maxWidth, resizeQuality: "medium" });
      const size = canvasSize(bitmap.width, bitmap.height, maxWidth);
      if (size) {
        const blob = await drawBlob(bitmap, size.width, size.height, quality);
        bitmap.close?.();
        return blob;
      }
      bitmap.close?.();
    } catch {
      /* Fall back to HTMLImageElement for formats createImageBitmap rejects. */
    }
  }
  const image = await loadImage(file);
  const size = canvasSize(image.width, image.height, maxWidth);
  if (!size) throw new Error("This photo could not be read. Try another picture of the thermometer.");
  return drawBlob(image, size.width, size.height, quality);
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    image.src = url;
  });
}

function drawBlob(source: CanvasImageSource, width: number, height: number, quality: number): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(source, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error("Failed to compress image"));
      else resolve(blob);
    }, "image/jpeg", quality);
  });
}
